// Get a number for the voice agent: pick one of the numbers Unanimity has
// ready (already routed to the platform), the agent that will answer it, and
// pay its monthly subscription on Stripe's page. Nobody types a number: a
// number we do not route would never reach the agent.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaLock } from 'react-icons/fa';
import { fail, formatPrice, startCheckout } from './api';

const BuyNumber = ({ offer, agents, defaultAgentId, onCancel, cancellable = true }) => {
    const { t, i18n } = useTranslation();
    const numbers = offer?.numbers || [];
    const [chosen, setChosen] = useState(numbers[0]?.phone_number || '');
    const [agentId, setAgentId] = useState(defaultAgentId || agents[0]?.id || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const price = formatPrice(offer?.monthly_price_eur, i18n.language);

    let blocked = '';
    if (!offer?.purchasable) {
        blocked = t('voiceSetup.buy.notPurchasable', 'Ce compte ne peut pas acheter de numéro en ligne : contactez le support.');
    } else if (!offer?.billing_ready) {
        blocked = t('voiceSetup.buy.billingUnavailable', 'Le paiement en ligne n’est pas disponible pour le moment : contactez le support.');
    } else if (numbers.length === 0) {
        blocked = t('voiceSetup.buy.noneAvailable', 'Aucun numéro disponible pour le moment : un nouveau lot est en commande. Revenez dans quelques heures.');
    }

    const pay = async () => {
        if (!chosen || busy) return;
        setBusy(true);
        setError('');
        try {
            const res = await startCheckout({ phone_number: chosen, agent_id: agentId || null });
            window.location.assign(res.data.checkout_url);
        } catch (err) {
            setError(fail(err, t('voiceSetup.buy.error', 'Le paiement n’a pas pu être préparé. Réessayez dans un instant.')));
            setBusy(false);
        }
    };

    return (
        <div className="vs-buy">
            <h4>{t('voiceSetup.buy.title', 'Choisissez votre numéro')}</h4>
            {blocked ? (
                <p className="vs-note">{blocked}</p>
            ) : (
                <>
                    <div className="vs-buy-numbers" role="radiogroup" aria-label={t('voiceSetup.buy.title', 'Choisissez votre numéro')}>
                        {numbers.map((n) => (
                            <button key={n.phone_number} type="button" role="radio" aria-checked={chosen === n.phone_number}
                                className={`vs-buy-number ${chosen === n.phone_number ? 'vs-buy-number--selected' : ''}`}
                                onClick={() => setChosen(n.phone_number)}>
                                <span>{n.display}</span>
                                {chosen === n.phone_number && <FaCheck aria-hidden="true" />}
                            </button>
                        ))}
                    </div>
                    <div className="vs-buy-grid">
                        <div className="vs-field">
                            <label className="vs-field">
                                <span>{t('voiceSetup.buy.agent', 'Agent qui répondra')}</span>
                                <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name || t('voiceSetup.unnamedAgent', 'Agent sans nom')}</option>)}
                                    <option value="">{t('voiceSetup.buy.agentLater', 'Choisir plus tard')}</option>
                                </select>
                            </label>
                            {agents.length === 0 && (
                                <small className="vs-note">
                                    {t('voiceSetup.buy.noAgent', 'Aucun agent vocal pour l’instant : le numéro ne répondra qu’une fois relié à un agent.')}{' '}
                                    <Link className="vs-link" to="/voice-agents">{t('voiceSetup.createAgentFirst', 'Créez d’abord un agent vocal')}</Link>
                                </small>
                            )}
                        </div>
                        <div className="vs-buy-summary">
                            <strong>{t('voiceSetup.buy.price', '{{price}} / mois', { price })}</strong>
                            <ul>
                                <li><FaCheck aria-hidden="true" /> {t('voiceSetup.buy.noCommitment', 'Sans engagement, résiliable à tout moment')}</li>
                                <li><FaCheck aria-hidden="true" /> {t('voiceSetup.buy.liveOnPayment', 'L’agent répond dès le paiement validé')}</li>
                                <li><FaCheck aria-hidden="true" /> {t('voiceSetup.buy.invoice', 'Une facture à chaque échéance')}</li>
                            </ul>
                        </div>
                    </div>
                    <p className="vs-note">
                        {t('voiceSetup.buy.keepYourNumber', 'Vous gardez votre numéro actuel ? Demandez à votre opérateur de le renvoyer vers ce numéro : vos clients appellent toujours le même numéro.')}
                    </p>
                </>
            )}
            {error && <p className="vs-error" role="alert">{error}</p>}
            <div className="vs-actions">
                {!blocked && (
                    <button type="button" className="u-btn u-btn--primary" onClick={pay} disabled={!chosen || busy}>
                        <FaLock aria-hidden="true" />
                        {busy ? t('voiceSetup.buy.redirecting', 'Ouverture du paiement…') : t('voiceSetup.buy.pay', 'Payer et activer')}
                    </button>
                )}
                {cancellable && (
                    <button type="button" className="u-btn u-btn--secondary" onClick={onCancel} disabled={busy}>
                        {t('voiceSetup.cancel', 'Annuler')}
                    </button>
                )}
                {!blocked && <span className="vs-fineprint">{t('voiceSetup.buy.stripe', 'Paiement sécurisé par Stripe')}</span>}
            </div>
        </div>
    );
};

export default BuyNumber;
